/* eslint-disable jsx-a11y/anchor-is-valid */
import { Alert, Box, Grid, Input, Link, tableBodyClasses } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { MagnifySvg } from '../../../../assets/magnifying-glass'
import { useMessages } from '../../../../providers/Messages/Messages.provider'
import {
  MessageThreadData,
  UploadObj,
} from '../../../../providers/Messages/types'
import { useProviderDetails } from '../../../../providers/ProviderDetails/ProviderDetails.provider'
import {
  WebSocketContext,
  WebSocketContextInterface,
} from '../../../../providers/WebSocket/WebSocketProvider'
import { themeColors } from '../../../../theme'
import Message from './Message.view'
import MessageEditor from './MessageEditor.view'
import MessageThread from './MessageThread.view'
import { useStyles } from './Messaging.styles'

type MessageTabConfig = {
  name: string
  hideOnMobile?: boolean
}

export interface MessageTabs {
  myPatients: MessageTabConfig
  providers: MessageTabConfig
  otherPatients: MessageTabConfig
  // NCA-47 Created new Message Tab
  pastPatients: MessageTabConfig
}

const tabs: MessageTabs = {
  myPatients: {
    name: 'My Patients',
  },
  providers: {
    name: 'Providers',
  },
  otherPatients: {
    name: 'All Patients',
    hideOnMobile: true,
  },
  // NCA-47 Created new Message Tab
  pastPatients: { name: 'Past Patients', hideOnMobile: true },
}

interface MessagingProps {
  isStandAloneView?: boolean
}

const Messaging = ({ isStandAloneView }: MessagingProps) => {
  const { classes } = useStyles()
  const [activeTab, setActiveTab] = useState<keyof MessageTabs>('myPatients')

  const { routedThreadId } = useParams()
  const navigate = useNavigate()

  const [uploads, setUploads] = useState<UploadObj[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchThreads, setSearchThreads] = useState<MessageThreadData[]>([])

  const { isConnected } = useContext(
    WebSocketContext
  ) as WebSocketContextInterface

  const {
    threads,
    currentThread,
    getProviderMessageThreads,
    getProviderMessagesInThread,
    getProviderPastPatientMessageThreads,
    attachmentKeys,
    setAttachmentKeys,
    isNewMessage,
    setIsNewMessage,
    selectedThreadId,
    setSelectedThreadId,
    navigatingDirectlyToThread,
    setNavigatingDirectlyToThread,
  } = useMessages()

  const { myProviderDetails } = useProviderDetails()

  useEffect(() => {
    if (routedThreadId) {
      if (!Number.isNaN(~~routedThreadId)) {
        setSelectedThreadId(Number(routedThreadId))
      }
    }
  }, [routedThreadId])

  const getInitialThreads = async () => {
    if (!navigatingDirectlyToThread) {
      await getProviderMessageThreads()
      setSearchThreads(threads[activeTab])
    }
    setNavigatingDirectlyToThread(false)
  }

  useEffect(() => {
    if (!routedThreadId) {
      getInitialThreads()
    }
  }, [])

  // check if getting any messages in thread; if not, provider(user) may not be in the thread; push to home
  const messageThreadCheck = async () => {
    if (typeof selectedThreadId === 'number') {
      try {
        const data = await getProviderMessagesInThread(selectedThreadId, 0)
        if (!data.messages.length) {
          navigate('/')
        }
      } catch (e) {
        console.log('messageThreadCheck-error', e)
      }
    }
  }

  useEffect(() => {
    if (myProviderDetails) {
      messageThreadCheck()
    }
  }, [selectedThreadId, myProviderDetails])

  const goToThread = (thread: MessageThreadData) => {
    setSelectedThreadId(thread.messageThreadId)
  }

  const backToMessages = async () => {
    setSelectedThreadId(null)
    setIsNewMessage(false)
    if (!isConnected) {
      await getProviderMessageThreads()
    }
  }

  useEffect(() => {
    if (activeTab === 'pastPatients') {
      getProviderPastPatientMessageThreads()
    }
  }, [activeTab])

  const renderTabs = (isStandAloneView?: boolean) => {
    return (
      <div className={classes.messaging__tabs}>
        {Object.entries(tabs).map(([key, val]: [string, MessageTabConfig]) => {
          return !(isStandAloneView && val.hideOnMobile) ? (
            <button
              key={`tab${key}`}
              className={classes.messaging__tab}
              onClick={() => {
                setActiveTab(key as keyof MessageTabs)
                setSelectedThreadId(null)
              }}
              style={{ fontWeight: activeTab === key ? 'bold' : '' }}
            >
              {tabs[key as keyof MessageTabs].name}
              {`(${
                threads[key as keyof MessageTabs]?.filter((m) => m.unread)
                  .length
              })`}
              {activeTab === key ? (
                <div className={classes.messaging__tab__active}></div>
              ) : null}
            </button>
          ) : null
        })}
      </div>
    )
  }

  useEffect(() => {
    if (searchTerm) {
      setSearchThreads(
        threads[activeTab].filter(
          (t: MessageThreadData) =>
            t.displayUser.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            t.displayUser.role.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    } else {
      setSearchThreads(threads[activeTab])
    }
  }, [searchTerm, activeTab, threads])

  // threads preview
  const renderMessageThreadPreviews = () => {
    if (selectedThreadId) {
      return null
    }
    return (
      <div
        className={classes.messaging__list}
        style={{
          height: '80%',
          overflow: 'scroll',
        }}
      >
        {renderSearchBar()}

        {searchThreads.map((thread, idx) => (
          <Grid item width={'100%'} key={idx} marginBottom={1}>
            <Message
              myProviderDetails={myProviderDetails}
              isStandAloneView={isStandAloneView}
              message={thread.messages[0]}
              keyValue={`mp${idx}`}
              unread={thread.unread}
              needsReview={
                thread.otherUsers.findIndex(
                  (u) => u.patientNeedsReview === true
                ) > -1
                  ? true
                  : false
              }
              openThread={() => goToThread(thread)}
              displayUser={thread.displayUser}
              displayThreadUserNames={thread.displayThreadUserNames}
              displayUserCount={thread.displayUserCount}
              patientId={thread.patientId}
            />
          </Grid>
        ))}
      </div>
    )
  }

  const renderSearchBar = () => {
    return (
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        alignSelf={'center'}
        width={'90%'}
        marginBottom={'20px'}
        border={'none'}
        borderRadius={'5px'}
      >
        <Input
          placeholder='Search by Name or MRN'
          style={{
            height: '20px',
            border: 'none',
            width: '95%',
            outline: 'none',
            marginRight: '15px',
          }}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
          }}
        />
        <MagnifySvg />
      </Box>
    )
  }

  // thread detail
  const renderMessageThread = () => {
    if (!selectedThreadId) {
      return null
    }

    if (currentThread) {
      return (
        <div className={classes.messaging__list}>
          <MessageThread
            activeTab={activeTab}
            myProviderDetails={myProviderDetails!}
            isStandAloneView={isStandAloneView}
            backToMessages={backToMessages}
            messageThread={currentThread}
            threadId={selectedThreadId}
            attachmentKeys={attachmentKeys}
            setAttachmentKeys={setAttachmentKeys}
            uploads={uploads}
            setUploads={setUploads}
          />
        </div>
      )
    }
  }

  const renderBackBtn = () => {
    return (
      <button
        type='button'
        onClick={backToMessages}
        className={classes.messageThread_backBtn}
      >
        &#8592;
      </button>
    )
  }

  return (
    <div
      style={{
        height: '100%',
      }}
    >
      <div>
        {myProviderDetails && !selectedThreadId
          ? !myProviderDetails?.companyPhone && (
              <Alert
                color='error'
                variant='filled'
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '15px',
                }}
              >
                Add Company Phone in your
                <Link
                  component='button'
                  underline='none'
                  color={(theme) => theme.palette.info.main}
                  onClick={() => navigate('/account')}
                  sx={{
                    padding: '2px 7px',
                    margin: '-2px 5px',
                    borderRadius: '10px',
                    background: `rgba(255, 255, 255, 0.6)`,
                    color: themeColors.blue40,
                    fontWeight: 'bold',
                  }}
                >
                  Account Settings
                </Link>
                to Receive notifications.
              </Alert>
            )
          : null}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}
      >
        {!selectedThreadId && renderTabs(isStandAloneView)}
      </div>
      {isNewMessage ? (
        // render newMessage editing box
        <div style={{ padding: '0 14px', height: '100%' }}>
          {renderBackBtn()}
          <MessageEditor
            myProviderDetails={myProviderDetails}
            isStandAloneView={isStandAloneView}
            uploads={uploads}
            setUploads={setUploads}
            setAttachmentKeys={setAttachmentKeys}
            setIsNewMessage={setIsNewMessage}
            isNewMessage={isNewMessage}
          />
        </div>
      ) : (
        <>
          {renderMessageThreadPreviews()}
          {renderMessageThread()}
        </>
      )}
    </div>
  )
}

export default Messaging
